import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatTermLabel,
  type RunStatus,
  type RunSummary,
} from "@canvas-sync/shared";
import { api } from "../api/client";
import { RunsTimeline } from "../components/RunsTimeline";
import { POLL, usePoll } from "../lib/usePoll";
import { useRole } from "../lib/roleContext";
import { isSubmissionInFlight, runSubmissionState } from "../lib/labels";
import { FilterBar, type FilterOption } from "../components/FilterBar";
import {
  CANVAS_ENV_OPTIONS,
  EMPTY_FILTERS,
  applyFilters,
  type Accessors,
  type FilterState,
} from "../lib/filters";
import type { ExpectedTerms } from "../lib/terms";

const NON_TERMINAL = new Set<RunStatus>(["QUEUED", "RUNNING"]);

const STATUS_OPTIONS: FilterOption[] = [
  { value: "QUEUED", label: "Waiting" },
  { value: "RUNNING", label: "In progress" },
  { value: "SUCCEEDED", label: "Done" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Canceled" },
];

const TRIGGER_OPTIONS: FilterOption[] = [
  { value: "manual", label: "Manual" },
  { value: "n8n", label: "Scheduled" },
  { value: "rerun", label: "Rerun" },
];

const RUN_ACCESSORS: Accessors<RunSummary> = {
  status: (r) => r.status,
  trigger: (r) => r.trigger,
  env: (r) => r.canvasEnv ?? "test",
  // Date used for range + newest/oldest sort. queuedAt is always present;
  // finishedAt would be a fine alternative but is absent on in-flight rows.
  date: (r) => r.queuedAt,
  duration: (r) => r.durationMs,
  searchFields: (r) => [r.runId, r.trigger, r.status, r.canvasEnv],
};

export function RunsPage() {
  const { isAdmin } = useRole();
  const [runs, setRuns] = useState<RunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  // Admin-entered expected terms (Settings page) — drive the Term-column
  // highlight. Best-effort: if the fetch fails the column still renders,
  // just without validation.
  const [expectedTerms, setExpectedTerms] = useState<ExpectedTerms>({});

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled)
          setExpectedTerms({
            primary: s.expectedPrimaryTerm,
            secondary: s.expectedSecondaryTerm,
          });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  usePoll(
    async () => {
      try {
        const result = await api.listRuns(200);
        setRuns(result.runs);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    () =>
      runs &&
      runs.some(
        (r) =>
          NON_TERMINAL.has(r.status) ||
          isSubmissionInFlight(runSubmissionState(r)),
      )
        ? POLL.LIST_ACTIVE_MS
        : POLL.IDLE_MS,
  );

  const visible = useMemo(
    () => (runs ? applyFilters(runs, filters, RUN_ACCESSORS) : []),
    [runs, filters],
  );

  return (
    <section>
      <div className="page-head">
        <h1>Syncs</h1>
        <div className="page-head-actions">
          <div className="muted">
            {runs ? `${runs.length} loaded` : "Loading…"}
          </div>
          {isAdmin && (
            <Link to="/syncs/new" className="btn">
              New sync
            </Link>
          )}
        </div>
      </div>

      <ExpectedTermsStrip expectedTerms={expectedTerms} isAdmin={isAdmin} />

      {runs && (
        <FilterBar
          value={filters}
          onChange={setFilters}
          statuses={STATUS_OPTIONS}
          triggers={TRIGGER_OPTIONS}
          envs={CANVAS_ENV_OPTIONS}
          searchPlaceholder="Search by sync ID, status, or how it started…"
          totalCount={runs.length}
          filteredCount={visible.length}
        />
      )}

      <div className="card">
        {error && <div className="error">{error}</div>}
        {runs === null && !error && <div className="muted">Loading…</div>}
        {runs?.length === 0 && <div className="muted">No syncs yet.</div>}
        {runs && runs.length > 0 && visible.length === 0 && (
          <div className="muted">No syncs match these filters.</div>
        )}
        {visible.length > 0 && (
          <RunsTimeline runs={visible} expectedTerms={expectedTerms} />
        )}
      </div>
    </section>
  );
}

/**
 * Read-only banner showing the admin-entered expected terms, so EVERY
 * role — not just admins, who can open Settings — can see exactly which
 * 4-digit term each feed is supposed to carry and compare it against the
 * Term column below. Editing stays on the (admin-only) Settings page.
 * Display-only today; eventually admins will trigger which term files
 * PeopleSoft extracts via a web service, and this is its surface.
 */
function ExpectedTermsStrip({
  expectedTerms,
  isAdmin,
}: {
  expectedTerms: ExpectedTerms;
  isAdmin: boolean;
}) {
  const { primary, secondary } = expectedTerms;
  return (
    <div className="muted small" style={{ margin: "2px 0 12px" }}>
      Expected terms —{" "}
      <span title="Every scheduled sync except the 2 AM one">
        hourly (primary):{" "}
        <span className="mono">
          {primary ? formatTermLabel(primary) : "not set"}
        </span>
      </span>
      {" · "}
      <span title="The 2 AM (Pacific) scheduled sync">
        2 AM (secondary):{" "}
        <span className="mono">
          {secondary ? formatTermLabel(secondary) : "not set"}
        </span>
      </span>
      {" — "}display only; a ticket is required to change the actual feed terms.
      {isAdmin && (
        <>
          {" "}
          <Link to="/settings" className="muted-link">
            Edit in Settings
          </Link>
        </>
      )}
    </div>
  );
}
